import React, { useEffect, useState, useRef } from 'react';
import { uploadFile } from '../../api/upload';
import { fetchFolders } from '../../api/cloud';
import { toast, ToastContainer } from 'react-toastify';

import { FaUpload } from 'react-icons/fa';

const FileUploader = ({ groups }) => {
  const [files, setFiles] = useState([]); // multiple files
  const [groupId, setGroupId] = useState('');
  const [folder, setFolder] = useState('');
  const [existingFolders, setExistingFolders] = useState([]);
  const [newFolder, setNewFolder] = useState('');
  const [uploadProgress, setUploadProgress] = useState({}); // progress per file
  const dropRef = useRef(null);

  useEffect(() => {
    const loadFolders = async () => {
      try {
        const res = await fetchFolders();
        setExistingFolders(res.data.folders || []);
      } catch (error) {
        toast.error('Failed to load folders');
      }
    };
    loadFolders();
  }, []);

  // Drag and drop handlers
  useEffect(() => {
    const div = dropRef.current;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      div.style.borderColor = '#007bff';
      div.style.backgroundColor = '#e9f5ff';
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      div.style.borderColor = '#ccc';
      div.style.backgroundColor = 'white';
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      div.style.borderColor = '#ccc';
      div.style.backgroundColor = 'white';

     const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
  ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(f.type)
);
      if (droppedFiles.length === 0) {
        toast.warning('Only PDF files are supported!');
        return;
      }
      setFiles(prev => [...prev, ...droppedFiles]);
    };

    div.addEventListener('dragover', handleDragOver);
    div.addEventListener('dragleave', handleDragLeave);
    div.addEventListener('drop', handleDrop);

    return () => {
      div.removeEventListener('dragover', handleDragOver);
      div.removeEventListener('dragleave', handleDragLeave);
      div.removeEventListener('drop', handleDrop);
    };
  }, []);

  const handleFileChange = (e) => {
   const selectedFiles = Array.from(e.target.files).filter(f =>
  ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(f.type)
);
    if (selectedFiles.length === 0) {
      toast.warning('Only PDF files are supported!');
      return;
    }
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleUpload = async () => {
    if (files.length === 0) return toast.warning('📂 Please select at least one file before uploading.');
    if (!groupId) return toast.warning('👥 Please select a group before uploading.');

    const selectedGroup = groups.find(g => g._id === groupId);
    const groupName = selectedGroup?.name?.toLowerCase().replace(/[^a-z0-9_\-]/g, '_') || groupId;
    const finalFolder = newFolder.trim() || folder;

    setUploadProgress({});

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('groupId', groupId);
      formData.append('groupName', groupName);
      formData.append('folder', finalFolder);

      try {
         const res = await uploadFile(formData, (progressEvent) => {
          if (progressEvent.total && progressEvent.loaded) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
          }
        });
      
      if (res?.data?.skipped) {
        toast.info(`⚠️ ${file.name} was already uploaded and skipped.`);
      } else {
        toast.success(`✅ ${file.name} uploaded and processed successfully!`);
      }

      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`❌ Failed to upload ${file.name}.`);
      }
    }

    setFiles([]);
    setNewFolder('');
    setUploadProgress({});
  };

  const removeFile = (fileName) => {
    setFiles(files.filter(f => f.name !== fileName));
    setUploadProgress(prev => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    });
  };

  return (
    <div style={{
      marginBottom: '1rem',
      padding: '1rem',
      border: '1px solid #ccc',
      borderRadius: '6px',
      width: '100%',
      maxWidth: '500px',
    }}>
     
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FaUpload size={30} />
        Upload Files
      </h3>

      <label>Select Group:</label>
      <select
        value={groupId}
        onChange={(e) => setGroupId(e.target.value)}
        style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem' }}
      >
        <option value="">-- Select Group --</option>
        {groups.map(g => (
          <option key={g._id} value={g._id}>{g.name}</option>
        ))}
      </select>

      <label>Select Existing Folder (Optional):</label>
      <select
        value={folder}
        onChange={(e) => setFolder(e.target.value)}
        style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem' }}
      >
        <option value="">-- No Folder --</option>
        {existingFolders.map(f => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>

      <label>Or Create New Folder:</label>
      <input
        type="text"
        placeholder="e.g., reports/q2"
        value={newFolder}
        onChange={(e) => setNewFolder(e.target.value)}
        style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem' }}
      />

      <label>Drag & Drop PDF Files Here or Click to Select:</label>
      <div
        ref={dropRef}
        style={{
          border: '2px dashed #ccc',
          borderRadius: '6px',
          padding: '1rem',
          marginBottom: '1rem',
          textAlign: 'center',
          cursor: 'pointer',
          color: '#666',
        }}
        onClick={() => document.getElementById('fileInput').click()}
      >
        {files.length === 0 ? 'Drop PDFs here or click to browse' : `${files.length} file(s) selected`}
      </div>

      <input
        id="fileInput"
        type="file"
        accept=".pdf,.docx,.xlsx"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* List selected files with progress and remove option */}
      {files.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {files.map((file) => (
            <div key={file.name} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
              padding: '6px 10px',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'black' }}>
  {file.name}
</span>
              <div style={{ width: '120px', marginRight: '12px' }}>
                <div style={{
                  width: '100%',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '8px',
                  height: '16px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
                }}>
                  <div
                    style={{
                      width: `${uploadProgress[file.name] || 0}%`,
                      height: '100%',
                      backgroundColor: '#28a745',
                      transition: 'width 0.3s ease',
                      borderRadius: '8px 0 0 8px',
                    }}
                  />
                </div>
              </div>
              <button
                onClick={() => removeFile(file.name)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#dc3545',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '18px',
                  lineHeight: '1',
                }}
                title="Remove file"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleUpload}
        style={{
          padding: '0.6rem 1.2rem',
          backgroundColor: '#007bff',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          width: '100%',
          fontWeight: 'bold',
        }}
      >
        Upload All
      </button>
    </div>
  );
};

export default FileUploader;
